import { Check, ChevronRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Badge } from '@/components/ui/badge';

/**
 * `✓ Mesh › ✓ Simulate › Analyze` — where the reader is in the journey.
 *
 * It lives in `StudyHeader`'s STEPPER slot rather than in `AnalyzeControlBar`,
 * because it says where the STUDY is and not what this page is showing. The
 * strip below the header is controls — things the reader operates; this is
 * state they read.
 *
 * The stepper slot specifically — right-justified, under `ml-auto` — because
 * that is where the `JourneyStrip` step-clicker sits on every other step, and
 * this is the same reading of the same journey. It spent a release in the
 * `trailing` slot instead, where it sat against the status pill and read as
 * one more badge on the study's name.
 *
 * Simulate remains a button because Results unlocks as soon as one design
 * point completes. While the remaining points run, it has no completion check
 * and gives the reader a direct route back to live progress.
 */
export function AnalyzePhaseBreadcrumb({
  onSimulateClick,
  simulationsRunning = false,
}: {
  onSimulateClick: () => void;
  simulationsRunning?: boolean;
}) {
  const { t } = useTranslation();
  return (
    <div
      className="hidden items-center gap-1.5 text-2xs text-muted-foreground sm:flex"
      aria-label={t('simopsShared.studyHeader.journeyLabel')}
    >
      <span className="inline-flex items-center gap-1">
        <Check
          className="h-3 w-3 text-success-strong"
          strokeWidth={3}
          aria-hidden
        />
        {t('simopsShared.studyHeader.phaseMesh')}
        <ChevronRight className="h-3 w-3 text-border" aria-hidden />
      </span>
      <button
        type="button"
        onClick={onSimulateClick}
        className="inline-flex items-center gap-1 rounded-sm hover:text-foreground focus-visible:outline-2 focus-visible:outline-ring"
      >
        {!simulationsRunning && (
          <Check
            className="h-3 w-3 text-success-strong"
            strokeWidth={3}
            aria-hidden
          />
        )}
        {t('simopsShared.studyHeader.phaseSimulate')}
        <ChevronRight className="h-3 w-3 text-border" aria-hidden />
      </button>
      <Badge size="xs">{t('simopsShared.studyHeader.phaseAnalyze')}</Badge>
    </div>
  );
}
